import type { AddCatalogItemToRegistryPayload, AddUniversalSearchItemToRegistryPayload } from '@apps/registry/common/components/Catalog/Catalog.controller';
import type { AddRegistryMethodType, CatalogPageTelemetry } from '@apps/registry/common/components/Catalog/Catalog.telemetry.types';
import type { DeliverableItemType, DonationFundItemType } from '@apps/registry/common/components/Catalog/utils/interpretItemType';
import type { CatalogProductSharedDisplayFieldsFragment } from '../../../../common/components/Catalog/api/shared.graphql.generated';
import { useCallback, useState } from 'react';

export type UseAddItemToRegistryAttemptReturn = ReturnType<typeof useAddItemToRegistryAttempt>;

type AddFromFriendsRegistryAttempt = {
  type: 'transitionalRegistryBanner';
};

type PdpAddAttempt = {
  // This is when user adds from PDP page
  type: 'item';
} & AddCatalogItemToRegistryPayload;

type SearchResultAddAttempt = {
  type: 'universalSearch';
} & AddUniversalSearchItemToRegistryPayload;

export type CashOrCharityAddAttempt<Product = CatalogProductSharedDisplayFieldsFragment> = {
  type: 'catalog';
  // This is when user adds from Catalog product grid view, particularly, cash items
  itemType: DonationFundItemType;
  // `/shop` root has no locator, hence undefined is supported.
  catalogLocator: Maybe<string>;
  itemPosition: Maybe<number>;
  telemetryArgs: {
    method: AddRegistryMethodType;
    page: CatalogPageTelemetry;
  };
  category: AddCatalogItemToRegistryPayload['category'];
  curation: AddCatalogItemToRegistryPayload['curation'];
} & (
  | {
      // When creating from custom, product is null
      product: null;
      isCreateFromCustom: true;
    }
  | {
      // When not creating custom, product must be provided
      product: Product;
      isCreateFromCustom: false;
    }
);

export type DeliverableAddAttempt = {
  type: 'catalog';
  itemType: DeliverableItemType;
  // `/shop` root has no locator, hence undefined is supported.
  catalogLocator: Maybe<string>;
} & Omit<AddCatalogItemToRegistryPayload, 'onAddComplete'>;

export type TrackAttemptPayload<CashProduct = CatalogProductSharedDisplayFieldsFragment> =
  | AddFromFriendsRegistryAttempt
  | PdpAddAttempt
  | SearchResultAddAttempt
  | CashOrCharityAddAttempt<CashProduct>
  | DeliverableAddAttempt;

export type TrackAttemptFn = (payload: TrackAttemptPayload) => void;

export const useAddItemToRegistryAttempt = () => {
  const [addToRegistryAttemptInfo, setAddToRegistryAttemptInfo] = useState<TrackAttemptPayload | null>(null);

  const trackAttempt = useCallback<TrackAttemptFn>(payload => {
    setAddToRegistryAttemptInfo(payload);
  }, []);

  const resetAttempt = useCallback(() => {
    setAddToRegistryAttemptInfo(null);
  }, []);

  return {
    addToRegistryAttemptInfo,
    trackAttempt,
    resetAttempt,
  } as const;
};
